import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { ApiBaseUrlContext } from '../App';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDateByLocale } from '../utils/dateUtils'; 

// SVGs / CSS from File 2
import { ReactComponent as AddIcon } from '../assets/svg/icon-add.svg';
import { ReactComponent as RemoveItemIcon } from '../assets/svg/icon-remove-1.svg';
import { ReactComponent as FileIcon } from '../assets/svg/icon-file.svg';
import '../topmenu.css';
import '../forms.css';

const InventoryForm = () => {
  // ---------------------------
  // State from File 1 (plus the same from File 2)
  // ---------------------------
  const [formData, setFormData] = useState({
    item_name: '',
    item_type: '',
    category: '',
    model_number: '',
    manufacturer: '',
    purchase_date: '',
    warranty_expiration: '',
    acquisition_method: '',
    assigned_to: '',
    assigned_location: '',
    item_status: '',
    status_flag: '',
    condition_description: '',
    estimated_value: '',
    supplier: '',
    inspection_schedule: '',
    notes: '',
    next_maintenance: '',
    maintenance_interval: '',
    next_calibration: '',
    calibration_interval: '',
  });

  const { t, i18n } = useTranslation();
  const [files, setFiles] = useState([]);            // New files to be uploaded
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [modalFile, setModalFile] = useState(null);
  const [previews, setPreviews] = useState([]);       // Local image/PDF previews
  const [dragOver, setDragOver] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({
    categories: [],
    manufacturers: [],
    locations: [],
    itemTypes: [],
    technicians: [],
  });

  const [error, setError] = useState(null);

  const apiBaseUrl = useContext(ApiBaseUrlContext);
  const navigate = useNavigate();
  const { id } = useParams();

  // ---------------------------
  // Helper to convert timestamps to yyyy-MM-dd
  // ---------------------------
  const convertTimestampToDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toISOString().split('T')[0];
  };

  // ---------------------------
  // Fetching data on mount (File 1 + File 2 logic)
  // ---------------------------
  useEffect(() => {
    // If editing, fetch the existing item
    if (id) {
      const fetchItem = async () => {
        try {
          const response = await axios.get(`${apiBaseUrl}/mnt/api/inventory/${id}`);
          const itemData = response.data;
      
          // Convert document_files as before...
          let transformedFiles = [];
          if (itemData.document_files) {
            try {
              transformedFiles = JSON.parse(itemData.document_files).map((file) => ({
                ...file,
                url: `${apiBaseUrl}/mnt/api/uploads/machine/${file.filename}`,
              }));
            } catch (parseError) {
              console.warn('Failed to parse document_files:', parseError.message);
            }
          }
      
          setFormData({
            ...itemData,
            // Convert dates as before...
            purchase_date: itemData.purchase_date
              ? convertTimestampToDate(itemData.purchase_date)
              : '',
            warranty_expiration: itemData.warranty_expiration
              ? convertTimestampToDate(itemData.warranty_expiration)
              : '',
            inspection_schedule: itemData.inspection_schedule
              ? convertTimestampToDate(itemData.inspection_schedule)
              : '',
            next_maintenance: itemData.next_maintenance || '',
            next_calibration: itemData.next_calibration || '',
            // **Ensure assigned_to is stored as a string**
            assigned_to: itemData.assigned_to ? itemData.assigned_to.toString() : '',
          });
          setUploadedFiles(transformedFiles);
        } catch (err) {
          setError('Error fetching item data');
          console.error('Error fetching item:', err);
        }
      };      

      fetchItem();
    }

    // Fetch unique options for dropdowns
    const fetchDropdownOptions = async () => {
      try {
        const [
          categoriesRes,
          manufacturersRes,
          locationsRes,
          itemTypesRes,
          techniciansRes,
        ] = await Promise.all([
          axios.get(`${apiBaseUrl}/mnt/api/inventory/unique/category`),
          axios.get(`${apiBaseUrl}/mnt/api/inventory/unique/manufacturer`),
          axios.get(`${apiBaseUrl}/mnt/api/inventory/unique/assigned_location`),
          axios.get(`${apiBaseUrl}/mnt/api/inventory/unique/item_type`),
          axios.get(`${apiBaseUrl}/mnt/api/technicians`),
        ]);

        setDropdownOptions({
          categories: categoriesRes.data,
          manufacturers: manufacturersRes.data,
          locations: locationsRes.data,
          itemTypes: itemTypesRes.data,
          technicians: techniciansRes.data,
        });
      } catch (err) {
        console.error('Error fetching dropdown options:', err);
      }
    };

    fetchDropdownOptions();
  }, [apiBaseUrl, id]);

  // ---------------------------
  // Handlers for form changes, file changes, etc.
  // ---------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleFileChange = (e) => {
    // Convert the FileList to an array
    const newFiles = Array.from(e.target.files);
    
    // Update the state with the newly selected files
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    
    // Generate previews for each new file (if needed for display)
    const newPreviews = newFiles.map(file => URL.createObjectURL(file));
    setPreviews((prevPreviews) => [...prevPreviews, ...newPreviews]);
  
    // Optionally, notify the user that files have been added
    if (newFiles.length > 0) {
      triggerFeedback(t('inventoryForm.feedback.filesAdded', { count: newFiles.length }));
    }
  };
  

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = [];
    const validPreviews = [];

    droppedFiles.forEach((file) => {
      if (file.size > 256 * 1024 * 1024) { 
        triggerFeedback(t('inventoryForm.errors.fileSizeExceeded', { fileName: file.name }));
        return;
      }
      
      // ✅ FULL MIME TYPE SUPPORT
      const allowedMimetypes = [
        'image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/svg+xml',
        'application/pdf', 'text/plain', 'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/zip', 'application/x-zip-compressed', 'multipart/x-zip',
        'application/octet-stream', 'application/x-rar-compressed',
        'application/dwg', 'application/cad', 'application/dxf',
        'application/iges', 'application/step'
      ];
    
      if (!allowedMimetypes.includes(file.type)) {
        triggerFeedback(t('inventoryForm.errors.unsupportedFileType', { fileName: file.name }));
        return;
      }
    
      validFiles.push(file);
      validPreviews.push(URL.createObjectURL(file));
    });

    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
    setPreviews((prevPreviews) => [...prevPreviews, ...validPreviews]);

    if (validFiles.length > 0) {
      triggerFeedback(t('inventoryForm.feedback.filesAddedDragDrop', { count: validFiles.length }));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const removeFile = (index) => {
    URL.revokeObjectURL(previews[index]);
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    setPreviews((prevPreviews) => prevPreviews.filter((_, i) => i !== index));
    triggerFeedback(t('inventoryForm.feedback.fileRemoved'));
  };

  const removeUploadedFile = async (filename) => {
    try {
      // Remove physically from server & from DB references
      await axios.delete(`${apiBaseUrl}/mnt/api/uploads/machine/${filename}`);
      await axios.delete(`${apiBaseUrl}/mnt/api/inventory/upload/${id}/${filename}`);
      setUploadedFiles((prev) => prev.filter((file) => file.filename !== filename));
      triggerFeedback(t('inventoryForm.feedback.fileRemoved'));
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Error deleting file');
    }
  };

  const triggerFeedback = (message) => {
    setFeedback(message);
    setShowFeedback(true);
    setTimeout(() => {
      setShowFeedback(false);
      setFeedback(null);
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
  
    try {
      const formDataObject = new FormData();
      Object.keys(formData).forEach((key) => {
        // If key is assigned_to and has a value, convert it to a number.
        if (key === 'assigned_to' && formData[key] !== '') {
          formDataObject.append(key, parseInt(formData[key], 10));
        } else {
          formDataObject.append(key, formData[key]);
        }
      });
  
      // Append new files only
      if (files.length > 0) {
        files.forEach((file) => formDataObject.append('inventoryFiles', file));
      }
  
      const endpoint = id
        ? `${apiBaseUrl}/mnt/api/inventory/${id}`
        : `${apiBaseUrl}/mnt/api/inventory`;
      const method = id ? 'put' : 'post';
  
      await axios[method](endpoint, formDataObject, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
  
      navigate('/inventory');
    } catch (err) {
      setError('Error saving item');
      console.error('Error during submission:', err);
    }
  };
  

  // ---------------------------
  // Render / Return
  // ---------------------------
  return (
    <>
      <div className="modal-full">

        {/* Top menu with Save / Cancel (from File 2) */}
        <div className="top-menu">

          <button 
            type="button" 
            className="button--blue" 
            onClick={handleSubmit}
          >
            {id ? t('inventoryForm.buttons.save') : t('inventoryForm.buttons.save')}
          </button>

          <div className="top-menu__button-space"></div>

          <button 
            type="button" 
            className="button--grey" 
            onClick={() => navigate('/inventory')}
          >
            {t('inventoryForm.buttons.close')}
          </button>
        </div>

        {/* Error or feedback messages */}
        {error && <div className="info-message">{error}</div>}
        {feedback && <div className="info-message">{feedback}</div>}

        <div className="content__main-title">
          {id ? t('inventoryForm.titles.editItem') : t('inventoryForm.titles.newItem')}
        </div>

        <form onSubmit={handleSubmit} id="inventoryForm">
          {/* ---------------------- */}
          {/* Details Section        */}
          {/* ---------------------- */}
          <div className="form-section__title">{t('inventoryForm.sections.details')}</div>
          <div className="form-section">

            {/* Item Name */}
            <div className="entry-item entry-item__medium">
              <label className="entry-label">{t('inventoryForm.labels.itemName')}</label>
              <input
                type="text"
                name="item_name"
                value={formData.item_name}
                onChange={handleChange}
                required
              />
            </div>

            {/* Item Type */}
            <div className="entry-item entry-item__content-width">
  <label className="entry-label">{t('inventoryForm.labels.itemType')}</label>
  <select
    name="item_type"
    value={formData.item_type}
    onChange={handleChange}
    className="form-select-drop"
  >
    <option value="">{t('inventoryForm.dropdown.selectItemType')}</option>
    {dropdownOptions.itemTypes.map((type, index) => (
      <option key={index} value={type}>
        {type}
      </option>
    ))}
  </select>
</div>

<div className="entry-item entry-item__medium">
  <input
    type="text"
    placeholder={t('inventoryForm.labels.orEnterNewItemType')}
    onChange={(e) =>
      setFormData((prev) => ({ ...prev, item_type: e.target.value }))
    }
  />
</div>
            {/* Category */}
            <div className="entry-item entry-item__content-width">
              <label className="entry-label">{t('inventoryForm.labels.category')}</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={`form-select-drop`}
              >
                <option value="">{t('inventoryForm.dropdown.selectCategory')}</option>
                {dropdownOptions.categories.map((cat, index) => (
                  <option key={index} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div className="entry-item entry-item__medium">
              <input
                type="text"
                placeholder={t('inventory.OrEnterANewCategoryName')}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, category: e.target.value }))
                }
              />
            </div>

            {/* Manufacturer */}
            <div className="entry-item entry-item__content-width">
              <label className="entry-label">{t('inventoryForm.labels.manufacturer')}</label>
              <select
                name="manufacturer"
                value={formData.manufacturer}
                onChange={handleChange}
                className={`form-select-drop`}
              >
                <option value="">{t('inventoryForm.dropdown.selectManufacturer')}</option>
                {dropdownOptions.manufacturers.map((man, index) => (
                  <option key={index} value={man}>
                    {man}
                  </option>
                ))}
              </select>
            </div>

            <div className="entry-item entry-item__medium">
              <input
                type="text"
                placeholder={t('inventory.OrEnterANewManufacturer')}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, manufacturer: e.target.value }))
                }
              />
            </div>

            {/* Model Number */}
            <div className="entry-item entry-item__medium">
              <label className="entry-label">{t('inventoryForm.labels.modelNumber')}</label>
              <input
                type="text"
                name="model_number"
                value={formData.model_number}
                onChange={handleChange}
              />
            </div>

            {/* Purchase Date */}
            <div className="entry-item entry-item__date">
              <label className="entry-label">{t('inventoryForm.labels.purchaseDate')}</label>
              <input
                type="date"
                name="purchase_date"
                value={
                  formData.purchase_date
                    ? new Date(formData.purchase_date).toISOString().split('T')[0]
                    : ''
                }
                onChange={handleChange}
              />
            </div>

            {/* Acquisition Method */}
            <div className="entry-item entry-item__medium">
              <label className="entry-label">{t('inventoryForm.labels.acquisitionMethod')}</label>
              <input
                type="text"
                name="acquisition_method"
                value={formData.acquisition_method}
                onChange={handleChange}
              />
            </div>

            {/* Warranty Expiration */}
            <div className="entry-item entry-item__date">
              <label className="entry-label">{t('inventoryForm.labels.warrantyExpiration')}</label>
              <input
                type="date"
                name="warranty_expiration"
                value={
                  formData.warranty_expiration
                    ? new Date(formData.warranty_expiration).toISOString().split('T')[0]
                    : ''
                }
                onChange={handleChange}
              />
            </div>

            {/* Supplier */}
            <div className="entry-item entry-item__medium">
              <label className="entry-label">{t('inventoryForm.labels.supplier')}</label>
              <input
                type="text"
                name="supplier"
                value={formData.supplier}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* ---------------------- */}
          {/* Assigned to Section    */}
          {/* ---------------------- */}
          <div className="form-section__title">{t('inventoryForm.sections.assignedTo')}</div>
          <div className="form-section">
            {/* 
              We'll unify this to a single 'Assigned to' (technicians) dropdown 
              from the File 1 approach 
            */}
           <div className="entry-item entry-item__small">
            <label className="entry-label">{t('inventoryForm.labels.technician')}</label>
            <select
              name="assigned_to"
              value={formData.assigned_to}
              onChange={handleChange}
              className={`form-select-drop`}
            >
              <option value="">{t('inventoryForm.dropdown.selectTechnician')}</option>
              {dropdownOptions.technicians.map((technician) => (
                <option key={technician.userid} value={technician.userid.toString()}>
                  {`${technician.username} (${technician.userfirstname} ${technician.userlastname})`}
                </option>
              ))}
            </select>
          </div>


            {/* assigned_location */}
            <div className="entry-item entry-item__content-width">
              <label className="entry-label">{t('inventoryForm.labels.location')}</label>
              <select
                name="assigned_location"
                value={formData.assigned_location}
                onChange={handleChange}
                className={`form-select-drop`}
              >
                <option value="">{t('inventoryForm.dropdown.selectLocation')}</option>
                {dropdownOptions.locations.map((loc, index) => (
                  <option key={index} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
            </div>

            <div className="entry-item entry-item__medium">
              <input
                type="text"
                placeholder={t('inventory.orEnterNewLocation')}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    assigned_location: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          {/* ---------------------- */}
          {/* Condition Section      */}
          {/* ---------------------- */}
          <div className="form-section__title">{t('inventoryForm.sections.condition')}</div>
          <div className="form-section">
            {/* item_status */}
            <div className="entry-item entry-item__medium">
              <label className="entry-label">{t('inventoryForm.labels.itemStatus')}</label>
              <input
                type="text"
                name="item_status"
                value={formData.item_status}
                onChange={handleChange}
              />
            </div>

            {/* status_flag */}
            <div className="entry-item entry-item__content-width">
              <label className="entry-label">{t('inventoryForm.labels.statusFlag')}</label>
              <select
                name="status_flag"
                value={formData.status_flag}
                onChange={handleChange}
                className={`form-select-drop`}
                disabled // 🔒 Disable if status should not be user-editable
              >
                <option value="">{t('inventoryForm.dropdown.selectStatus')}</option>
                <option value="CHECKED_IN">{t('inventory.checkedIn')}</option>
                <option value="CHECKED_OUT">{t('inventory.checkedOut')}</option>
              </select>
            </div>

            {/* condition_description */}
            <div className="entry-item entry-item__medium">
              <label className="entry-label">{t('inventoryForm.labels.conditionDescription')}</label>
              <textarea
                name="condition_description"
                value={formData.condition_description}
                onChange={handleChange}
              />
            </div>

            {/* estimated_value */}
            <div className="entry-item entry-item__8-digits">
              <label className="entry-label">{t('inventoryForm.labels.estimatedValue')}</label>
              <input
                type="number"
                name="estimated_value"
                value={formData.estimated_value}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* ---------------------- */}
          {/* Schedule Section       */}
          {/* ---------------------- */}
          <div className="form-section__title">{t('inventoryForm.sections.schedule')}</div>
          <div className="form-section">
            {/* inspection_schedule 
            <div className="entry-item entry-item__date">
              <label className="entry-label">{t('inventoryForm.labels.inspectionSchedule')}</label>
              <input
                type="date"
                name="inspection_schedule"
                value={
                  formData.inspection_schedule
                    ? new Date(formData.inspection_schedule).toISOString().split('T')[0]
                    : ''
                }
                onChange={handleChange}
              />
            </div>  */}

            {/* maintenance_interval */}
            <div className="entry-item entry-item__8-digits">
              <label className="entry-label">{t('inventoryForm.labels.maintenanceInterval')}</label>
              <input
                type="number"
                name="maintenance_interval"
                value={formData.maintenance_interval || ''}
                onChange={handleChange}
                min="0"
              />
            </div>

            {/* next_maintenance (stored as timestamp in state) */}
            <div className="entry-item entry-item__date">
              <label className="entry-label">{t('inventoryForm.labels.nextMaintenance')}</label>
              <input
                type="date"
                name="next_maintenance"
                value={
                  formData.next_maintenance
                    ? new Date(formData.next_maintenance).toISOString().split('T')[0]
                    : ''
                }
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    next_maintenance: new Date(e.target.value).getTime(),
                  }))
                }
              />
            </div>

            {/* calibration_interval */}
            <div className="entry-item entry-item__8-digits">
              <label className="entry-label">{t('inventoryForm.labels.calibrationInterval')}</label>
              <input
                type="number"
                name="calibration_interval"
                value={formData.calibration_interval || ''}
                onChange={handleChange}
                min="0"
              />
            </div>

            {/* next_calibration (stored as timestamp) */}
            <div className="entry-item entry-item__date">
              <label className="entry-label">{t('inventoryForm.labels.nextCalibration')}</label>
              <input
                type="date"
                name="next_calibration"
                value={
                  formData.next_calibration
                    ? new Date(formData.next_calibration).toISOString().split('T')[0]
                    : ''
                }
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    next_calibration: new Date(e.target.value).getTime(),
                  }))
                }
              />
            </div>
          </div>

          {/* ---------------------- */}
          {/* Notes Section          */}
          {/* ---------------------- */}
          <div className="form-section__title">{t('inventoryForm.sections.notes')}</div>
          <div className="form-section">
            <div className="entry-item entry-item__full-width">
              <label className="entry-label">{t('inventoryForm.labels.notes')}</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* ---------------------- */}
          {/* Files Section          */}
          {/* ---------------------- */}
          <div className="form-section__title">{t('inventoryForm.sections.files')}</div>
          <div
            className={`form-section ${dragOver ? 'form-section--drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Existing (uploaded) files */}
            <div className="file-list">
              {uploadedFiles.map((file, index) => (
                <div className="entry-item__file" key={index}>
                  <div className="entry-item__file-icon">
                    <FileIcon />
                  </div>
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="entry-item__file-link"
                  >
                    {file.filename}
                  </a>
                  <button
                    className="slim-button--remove"
                    type="button"
                    onClick={() => removeUploadedFile(file.filename)}
                  >
                    <RemoveItemIcon />
                  </button>
                </div>
              ))}
            </div>

            {/* File input for new files */}
            <div className="file-upload">
              <input
                type="file"
                id="files"
                name="inventoryFiles"
                className="file-upload__input"
                multiple
                onChange={handleFileChange}
              />
              <label htmlFor="files" className="file-upload__label">
                <button type="button" className="button--icon button--grey" onClick={() => document.getElementById('files').click()}>
                  <AddIcon />
                </button>
                <span className="file-upload__filename">{t(`inventory.Nofilechosen`)}</span>
              </label>
            </div>
          </div>

          {/* Preview newly added (not yet uploaded) files with remove button */}
          {previews.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  flexWrap: 'wrap',
                  marginTop: '10px',
                }}
              >
                {previews.map((preview, index) => (
                  <div key={index} style={{ position: 'relative' }}>
                    {/* For local previews, we won't differentiate PDF vs Image with an icon,
                        but you could do so if you wanted. 
                        By default, image tags won't display PDF. */}
                    <img
                      src={preview}
                      alt={`Preview ${index}`}
                      style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                      onClick={() => setModalFile(preview)}
                    />
                    <button
                      type="button"
                      style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        background: 'red',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer',
                      }}
                      onClick={() => removeFile(index)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Modal for previewing newly added files (or PDFs) */}
          {modalFile && (
            <div
              style={{
                position: 'fixed',
                top: '0',
                left: '0',
                width: '100%',
                height: '100%',
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              onClick={() => setModalFile(null)}
            >
              <div
                style={{
                  background: 'white',
                  padding: '20px',
                  borderRadius: '5px',
                  position: 'relative',
                }}
              >
                {modalFile.endsWith('.pdf') ? (
                  <iframe
                    src={modalFile}
                    style={{ width: '800px', height: '600px' }}
                    title="PDF Preview"
                  ></iframe>
                ) : (
                  <img
                    src={modalFile}
                    alt="Modal Preview"
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                )}
              </div>
            </div>
          )}

       
          {
            <div style={{ marginTop: '1rem' }}>
              <button type="submit" className="button--blue">
                {id ? t('inventory.updateItem') : t('inventory.createItem')}
              </button>
              <button
                type="button"
                className="button--grey"
                onClick={() => navigate('/inventory')}
                style={{ marginLeft: '10px' }}
              >
                {t(`inventory.Cancel`)}
              </button>
            </div>
          }
        </form>
      </div>
    </>
  );
};

export default InventoryForm;
